import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  UserPlus, Users, Shield, Edit, Trash2, Loader2, Hash, Lock, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { StaffStorePicker, StaffStoreLabel } from '../components/StaffStorePicker';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StaffManagement = () => {
  const { getAuthHeader } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    initials: '',
    pin: '',
    role: 'staff',
    stores: []
  });

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API_URL}/staff`, { headers: getAuthHeader() });
      setStaff(response.data);
    } catch (error) {
      // No staff yet is okay
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  const resetForm = () => {
    setFormData({ name: '', initials: '', pin: '', role: 'staff', stores: [] });
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Please enter staff name');
      return;
    }
    if (!formData.initials || formData.initials.length < 2) {
      toast.error('Please enter 2-3 letter initials');
      return;
    }
    if (!formData.pin || formData.pin.length !== 2) {
      toast.error('Please enter a 2-digit PIN');
      return;
    }
    
    setFormLoading(true);
    try {
      await axios.post(`${API_URL}/staff`, formData, { headers: getAuthHeader() });
      toast.success('Staff member added successfully');
      setIsAddDialogOpen(false);
      resetForm();
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add staff');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const updateData = {
        name: formData.name,
        role: formData.role,
        stores: formData.stores
      };
      
      // Only update initials/pin if provided
      if (formData.initials && formData.initials.length >= 2) {
        updateData.initials = formData.initials;
      }
      if (formData.pin && formData.pin.length === 2) {
        updateData.pin = formData.pin;
      }
      
      await axios.put(`${API_URL}/staff/${selectedStaff.id}`, updateData, { headers: getAuthHeader() });
      toast.success('Staff updated successfully');
      setIsEditDialogOpen(false);
      resetForm();
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update staff');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeactivate = async (member) => {
    if (!window.confirm(`Remove ${member.name} from your team?\n\nIf they have made sales they are kept as "inactive" so reports still show their name; otherwise they are removed completely.`)) return;
    try {
      const res = await axios.delete(`${API_URL}/staff/${member.id}`, { headers: getAuthHeader() });
      toast.success(res.data?.message || 'Staff member removed');
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove staff');
    }
  };

  const openEditDialog = (member) => {
    setSelectedStaff(member);
    setFormData({
      name: member.name,
      initials: member.initials || '',
      pin: '', // Don't pre-fill PIN
      role: member.role,
      stores: member.stores || []
    });
    setIsEditDialogOpen(true);
  };

  const activeStaff = staff.filter(m => m.active);
  const inactiveStaff = staff.filter(m => !m.active);
  const visibleStaff = showInactive ? staff : activeStaff;

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-red-100 text-red-700 border-red-200',
      manager: 'bg-blue-100 text-blue-700 border-blue-200',
      staff: 'bg-slate-100 text-slate-700 border-slate-200'
    };
    return styles[role] || styles.staff;
  };

  const getRolePermissions = (role) => {
    const permissions = {
      admin: 'Full access - all settings, reports, inventory',
      manager: 'POS, inventory, reports, can override margin warnings',
      staff: 'POS access only'
    };
    return permissions[role] || permissions.staff;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-management-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            Staff Management
          </h1>
          <p className="text-slate-500 mt-1">Manage your team members and their POS access</p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-orange-500 hover:bg-orange-600"
              onClick={resetForm}
              data-testid="add-staff-button"
            >
              <UserPlus className="w-4 h-4 mr-2" /> Add Staff
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold uppercase">Add Staff Member</DialogTitle>
              <DialogDescription>Staff can login with their initials and 2-digit PIN</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAdd} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  required
                  data-testid="staff-name-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="initials">Initials * (2-3 letters)</Label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="initials"
                      value={formData.initials}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        initials: e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3) 
                      })}
                      placeholder="JD"
                      className="pl-9 uppercase font-mono text-lg tracking-widest"
                      maxLength={3}
                      required
                      data-testid="staff-initials-input"
                    />
                  </div>
                  <p className="text-xs text-slate-500">Used for quick login</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="pin">PIN * (2 digits)</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="pin"
                      type="password"
                      value={formData.pin}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        pin: e.target.value.replace(/\D/g, '').slice(0, 2) 
                      })}
                      placeholder="••"
                      className="pl-9 text-center font-mono text-lg tracking-widest"
                      maxLength={2}
                      inputMode="numeric"
                      required
                      data-testid="staff-pin-input"
                    />
                  </div>
                  <p className="text-xs text-slate-500">2-digit code for login</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                  <SelectTrigger data-testid="staff-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">{getRolePermissions(formData.role)}</p>
              </div>

              <StaffStorePicker value={formData.stores} onChange={(stores) => setFormData({ ...formData, stores })} idPrefix="add-staff-store" />
              
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={formLoading} className="bg-slate-900 hover:bg-slate-800" data-testid="submit-staff">
                  {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Staff'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Staff Login Flow</p>
            <p>After opening the store (owner login), staff members can quickly clock in using their <strong>initials + 2-digit PIN</strong>. Each staff member's sales are tracked separately. Tick the store(s) each person works at so only your team shows on that store's login screen.</p>
          </div>
        </CardContent>
      </Card>

      {/* Staff List */}
      <Card className="border rounded-sm" data-testid="staff-table">
        <CardHeader className="border-b bg-slate-50">
          <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
            <Users className="w-5 h-5" /> Team Members ({activeStaff.length})
            {inactiveStaff.length > 0 && (
              <button type="button" onClick={() => setShowInactive(v => !v)} className="ml-auto text-xs font-normal normal-case text-slate-500 underline" data-testid="toggle-inactive-staff">
                {showInactive ? 'Hide' : 'Show'} {inactiveStaff.length} inactive
              </button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-heading font-bold uppercase text-xs">Name</TableHead>
                <TableHead className="font-heading font-bold uppercase text-xs">Initials</TableHead>
                <TableHead className="font-heading font-bold uppercase text-xs">Role</TableHead>
                <TableHead className="font-heading font-bold uppercase text-xs">Works at</TableHead>
                <TableHead className="font-heading font-bold uppercase text-xs">Status</TableHead>
                <TableHead className="font-heading font-bold uppercase text-xs">Added</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleStaff.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    No staff members yet. Add your first team member!
                  </TableCell>
                </TableRow>
              ) : (
                visibleStaff.map((member) => (
                  <TableRow key={member.id} data-testid={`staff-row-${member.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center">
                          <span className="text-sm font-bold text-slate-600">
                            {member.initials || member.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="font-medium">{member.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {member.initials ? (
                        <span className="font-mono text-lg font-bold text-slate-700 tracking-wider">
                          {member.initials}
                        </span>
                      ) : (
                        <span className="text-slate-400 text-sm">Not set</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={getRoleBadge(member.role)}>
                        <Shield className="w-3 h-3 mr-1" />
                        {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell data-testid={`staff-stores-${member.id}`}>
                      <StaffStoreLabel stores={member.stores} />
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={member.active ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}>
                        {member.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {formatDate(member.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(member)}
                          data-testid={`edit-staff-${member.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDeactivate(member)}
                          data-testid={`deactivate-staff-${member.id}`}
                          title="Remove from team"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase">Edit Staff Member</DialogTitle>
            <DialogDescription>Update staff member information. Leave PIN blank to keep current.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Full Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-initials">Initials (2-3 letters)</Label>
                <Input
                  id="edit-initials"
                  value={formData.initials}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    initials: e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3) 
                  })}
                  placeholder={selectedStaff?.initials || 'JD'}
                  className="uppercase font-mono"
                  maxLength={3}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-pin">New PIN (optional)</Label>
                <Input
                  id="edit-pin"
                  type="password"
                  value={formData.pin}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    pin: e.target.value.replace(/\D/g, '').slice(0, 2) 
                  })}
                  placeholder="••"
                  className="text-center font-mono"
                  maxLength={2}
                  inputMode="numeric"
                />
                <p className="text-xs text-slate-500">Leave blank to keep current PIN</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-role">Role</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">{getRolePermissions(formData.role)}</p>
            </div>

            <StaffStorePicker value={formData.stores} onChange={(stores) => setFormData({ ...formData, stores })} idPrefix="edit-staff-store" />
            
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={formLoading} className="bg-slate-900 hover:bg-slate-800" data-testid="update-staff">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffManagement;
